<template>
  <div class="robot-wallet-page">
    <div class="overview-header">
      <div class="page-title-group">
        <h1 class="page-title">
          <i class="el-icon-cpu"></i> 机器人集群钱包管理
        </h1>
        <p class="page-subtitle">实时监控节点状态与独立账户资产分布 (DeRAS 系统)</p>
      </div>

      <div class="global-stats">
        <div class="stat-item">
          <span class="stat-label">机器人总数</span>
          <span class="stat-value">{{ robotList.length }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">正在作业中</span>
          <span class="stat-value success-text">{{ onlineCount }}</span>
        </div>
        <div class="stat-item highlight">
          <span class="stat-label">集群待归集总额</span>
          <span class="stat-value gold-text">{{ formatNumber(totalPendingBalance) }} ROC</span>
        </div>
      </div>
    </div>

    <div class="toolbar">
      <div class="filter-group">
        <el-input 
          v-model="searchKeyword" 
          placeholder="搜索机器人 ID 或 钱包地址..." 
          prefix-icon="el-icon-search"
          clearable
          style="width: 260px;"
        ></el-input>
        
        <el-select v-model="statusFilter" placeholder="状态筛选" clearable style="width: 150px; margin-left: 10px;">
          <el-option label="全部状态" value=""></el-option>
          <el-option label="空闲 (0)" :value="0"></el-option>
          <el-option label="繁忙 (1)" :value="1"></el-option>
        </el-select>
      </div>

      <div class="action-group">
        <el-button 
          type="warning" 
          plain 
          icon="el-icon-coin" 
          :disabled="totalPendingBalance === 0"
          @click="handleCollectAll"
          :loading="collectingAll"
        >
          一键归集收益
        </el-button>
        <el-button type="primary" icon="el-icon-refresh" circle @click="fetchRobotList" :loading="loading"></el-button>
      </div>
    </div>

    <div class="table-container">
      <el-table 
        :data="paginatedList" 
        style="width: 100%" 
        v-loading="loading"
        header-cell-class-name="table-header-gray"
        row-key="id"
        border
      >
        <el-table-column label="节点信息" min-width="220">
          <template #default="scope">
            <div class="robot-info-cell">
              <div class="robot-avatar" :class="getStatusClass(scope.row.status)">
                <i class="el-icon-monitor"></i>
              </div>
              <div class="robot-details">
                <div class="robot-name">
                  {{ scope.row.name }} (ID: {{ scope.row.id }})
                  <el-tag size="mini" :type="getStatusTagType(scope.row.status)" effect="dark" style="margin-left: 8px;">
                    {{ getStatusText(scope.row.status) }}
                  </el-tag>
                </div>
                <div class="robot-address">
                  <span class="label">钱包:</span> 
                  <span class="address">{{ scope.row.walletAddress || '未分配' }}</span>
                  <i class="el-icon-document-copy copy-icon" @click="copyAddress(scope.row.walletAddress)" title="复制"></i>
                </div>
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="信誉得分" width="120" align="center">
          <template #default="scope">
            <el-statistic group-separator="," :value="scope.row.score" title="">
              <template slot="prefix">
                <i class="el-icon-star-on" style="color: #E6A23C"></i>
              </template>
            </el-statistic>
          </template>
        </el-table-column>

        <el-table-column label="独立钱包余额 (ROC)" min-width="180" sortable prop="money">
          <template #default="scope">
            <div class="wallet-cell">
              <span class="balance-value">{{ formatNumber(scope.row.money || 0) }}</span>
              <span class="balance-unit">ROC</span>
              <div class="balance-equivalent">
                ≈ ${{ ((scope.row.money || 0) * 0.15).toFixed(2) }} USD
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="操作" width="160" fixed="right" align="center">
          <template #default="scope">
            <div class="action-cell">
              <el-button 
                type="warning" 
                size="mini" 
                plain 
                icon="el-icon-coin"
                :disabled="(scope.row.money || 0) <= 0"
                @click="handleCollectSingle(scope.row)"
                :loading="scope.row.collecting"
              >
                归集
              </el-button>
              <el-button 
                type="info" 
                size="mini" 
                icon="el-icon-setting" 
                circle 
                @click="handleSettings(scope.row)"
              ></el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <div class="pagination-wrapper">
      <el-pagination
        background
        layout="total, sizes, prev, pager, next, jumper"
        :current-page="currentPage"
        :page-sizes="[10, 20, 50]"
        :page-size="pageSize"
        :total="filteredRobotList.length"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      ></el-pagination>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'RobotWalletList',
  data() {
    return {
      loading: false,
      collectingAll: false,
      searchKeyword: '',
      statusFilter: '',
      currentPage: 1,
      pageSize: 10,
      robotList: []
    };
  },
  computed: {
    // 筛选后的列表
    filteredRobotList() {
      let list = this.robotList;
      if (this.searchKeyword) {
        const key = this.searchKeyword.toLowerCase();
        list = list.filter(r => 
          r.id.toString().includes(key) || 
          r.name.toLowerCase().includes(key) ||
          (r.walletAddress && r.walletAddress.toLowerCase().includes(key))
        );
      }
      if (this.statusFilter !== '' && this.statusFilter !== null) {
        list = list.filter(r => r.status === this.statusFilter);
      }
      return list;
    },
    paginatedList() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.filteredRobotList.slice(start, end);
    },
    onlineCount() {
      // 0 是空闲，1 是繁忙/计算中
      return this.robotList.filter(r => r.status === 1).length;
    },
    totalPendingBalance() {
      return this.robotList.reduce((sum, r) => sum + (r.money || 0), 0);
    }
  },
  created() {
    this.fetchRobotList();
  },
  methods: {
    async fetchRobotList() {
      this.loading = true;
      try {
        const response = await axios.get('http://localhost:8090/user/getAllRoc');
        let list = [];
        // 处理多种可能的后端返回结构
        if (response.data && response.data.code === 200) {
          list = response.data.data;
        } else if (Array.isArray(response.data)) {
          list = response.data;
        }

        this.robotList = list.map(item => ({
          id: item.id,
          name: item.name,
          money: parseFloat(item.money) || 0,
          walletAddress: item.walAddress, // 接口字段是 walAddress
          status: item.status,           // 0空闲, 1繁忙
          score: item.score || 0,
          collecting: false
        }));

        this.$message.success('集群数据已同步');
      } catch (error) {
        this.$message.error('无法连接至机器人集群控制器');
      } finally {
        this.loading = false;
      }
    },

    formatNumber(num) {
      return Number(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
    },

    // 状态辅助 (0空闲, 1繁忙)
    getStatusClass(status) {
      return status === 1 ? 'status-working' : 'status-idle';
    },
    getStatusTagType(status) {
      return status === 1 ? 'danger' : 'success';
    },
    getStatusText(status) {
      return status === 1 ? '繁忙/作业中' : '节点空闲';
    },

    copyAddress(address) {
      if (!address) return this.$message.warning('地址无效');
      navigator.clipboard.writeText(address).then(() => {
        this.$message.success('地址已复制到剪贴板');
      });
    },

    handleCollectSingle(robot) {
      this.$confirm(`确认将 Node-${robot.id} 的资产归集到主钱包？`, '资产归集确认', {
        type: 'warning'
      }).then(() => {
        robot.collecting = true;
        // 模拟归集接口
        setTimeout(() => {
          robot.money = 0;
          robot.collecting = false;
          this.$notify({ title: '归集成功', type: 'success' });
        }, 1000);
      });
    },

    handleCollectAll() {
      this.collectingAll = true;
      this.$confirm(`准备归集全量资产 (${this.formatNumber(this.totalPendingBalance)} ROC)，是否继续？`, '批量操作', {
        confirmButtonText: '全量归集',
        type: 'warning'
      }).then(() => {
        setTimeout(() => {
          this.robotList.forEach(r => r.money = 0);
          this.collectingAll = false;
          this.$message.success('全量收益已入库');
        }, 1500);
      }).catch(() => { this.collectingAll = false; });
    },

    handleSettings(row) {
      this.$message.info(`调取 Node-${row.id} 详细运行参数...`);
    },

    handleSizeChange(val) { this.pageSize = val; this.currentPage = 1; },
    handleCurrentChange(val) { this.currentPage = val; }
  }
};
</script>

<style scoped>
.robot-wallet-page {
  padding: 24px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.overview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  background: #fff;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.05);
}

.page-title { margin: 0 0 8px 0; font-size: 24px; color: #303133; display: flex; align-items: center; gap: 12px; }
.page-subtitle { margin: 0; color: #909399; font-size: 14px; }

.global-stats { display: flex; gap: 30px; }
.stat-item { text-align: center; padding: 0 15px; border-right: 1px solid #ebeef5; }
.stat-item:last-child { border-right: none; }
.stat-label { display: block; font-size: 12px; color: #909399; margin-bottom: 6px; }
.stat-value { display: block; font-size: 20px; font-weight: bold; font-family: ui-monospace, monospace; }
.success-text { color: #67C23A; }
.gold-text { color: #E6A23C; }

.toolbar {
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
  background: #fff;
  padding: 16px;
  border-radius: 8px;
}

.table-container { background: #fff; border-radius: 8px; padding: 20px; box-shadow: 0 2px 12px 0 rgba(0,0,0,0.05); }

.robot-info-cell { display: flex; align-items: center; gap: 12px; }
.robot-avatar {
  width: 40px; height: 40px; border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  font-size: 20px; color: #fff; flex-shrink: 0;
}

/* 状态颜色：0空闲(蓝色/灰色), 1繁忙(红色/橙色) */
.status-working { background: linear-gradient(135deg, #f56c6c, #f89898); }
.status-idle { background: linear-gradient(135deg, #409eff, #79bbff); }

.robot-details { display: flex; flex-direction: column; gap: 4px; overflow: hidden; }
.robot-name { font-weight: bold; font-size: 14px; }
.address { font-family: ui-monospace, monospace; background: #f5f7fa; padding: 2px 6px; border-radius: 4px; font-size: 11px; }
.copy-icon { cursor: pointer; color: #409EFF; margin-left: 5px; }

.wallet-cell { display: flex; flex-direction: column; }
.balance-value { font-size: 16px; font-weight: bold; font-family: ui-monospace, monospace; color: #303133; }
.balance-unit { font-size: 11px; color: #909399; margin-left: 4px; }
.balance-equivalent { font-size: 11px; color: #909399; }

.pagination-wrapper { margin-top: 20px; display: flex; justify-content: flex-end; }
</style>